import { useState, useEffect, useCallback } from 'react';
import { useChatContext } from '../contexts/ChatContext';
import streamChatService from '../services/streamChat';

export const useStreamChat = () => {
  const context = useChatContext();
  return context;
};

export const useChannel = (channelId) => {
  const [channel, setChannel] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [typingUsers, setTypingUsers] = useState([]);
  
  const { client } = useChatContext();

  useEffect(() => {
    if (!client || !channelId) {
      setIsLoading(false);
      return;
    }

    let unsubscribeFunctions = [];

    const loadChannel = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const channelInstance = await streamChatService.getChannel(channelId);
        if (!channelInstance) {
          throw new Error('Channel not found');
        }

        setChannel(channelInstance);

        // Load initial messages
        const state = channelInstance.state;
        const formattedMessages = state.messages.map(msg => 
          streamChatService.formatMessageForUI(msg)
        );
        setMessages(formattedMessages);

        // Set up real-time listeners
        const handleNewMessage = channelInstance.on('message.new', (event) => {
          const formattedMessage = streamChatService.formatMessageForUI(event.message);
          setMessages(prev => [...prev, formattedMessage]);
        });

        const handleMessageUpdated = channelInstance.on('message.updated', (event) => {
          const formattedMessage = streamChatService.formatMessageForUI(event.message);
          setMessages(prev => prev.map(msg => 
            msg.id === formattedMessage.id ? formattedMessage : msg
          ));
        });

        const handleMessageDeleted = channelInstance.on('message.deleted', (event) => {
          setMessages(prev => prev.filter(msg => msg.id !== event.message.id));
        });

        const handleTypingStart = channelInstance.on('typing.start', (event) => {
          if (event.user.id !== client.userID) {
            const sanitizedUser = { 
              ...event.user, 
              name: streamChatService.getSafeDisplayName(event.user) 
            };
            setTypingUsers(prev => {
              const existing = prev.find(u => u.id === sanitizedUser.id);
              if (!existing) {
                return [...prev, sanitizedUser];
              }
              return prev.map(u => u.id === sanitizedUser.id ? sanitizedUser : u);
            });
          }
        });

        const handleTypingStop = channelInstance.on('typing.stop', (event) => {
          setTypingUsers(prev => prev.filter(u => u.id !== event.user.id));
        });

        unsubscribeFunctions = [
          handleNewMessage.unsubscribe,
          handleMessageUpdated.unsubscribe,
          handleMessageDeleted.unsubscribe,
          handleTypingStart.unsubscribe,
          handleTypingStop.unsubscribe
        ];

      } catch (err) {
        console.error('Failed to load channel:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    loadChannel();

    return () => {
      unsubscribeFunctions.forEach(fn => {
        if (typeof fn === 'function') fn();
      });
    };
  }, [client, channelId]);

  const sendMessage = useCallback(async (text, attachments = [], options = {}) => {
    if (!channel) return;

    try {
      // Pass through any additional options (like collab request fields)
      await streamChatService.sendMessage(channelId, { 
        text, 
        attachments,
        ...options,
      });
    } catch (err) {
      console.error('Failed to send message:', err);
      throw err;
    }
  }, [channel, channelId]);

  const sendTypingEvent = useCallback(async () => {
    if (!channel) return;
    await streamChatService.sendTypingEvent(channelId);
  }, [channel, channelId]);

  const stopTypingEvent = useCallback(async () => {
    if (!channel) return;
    await streamChatService.stopTypingEvent(channelId);
  }, [channel, channelId]);

  const deleteMessage = useCallback(async (messageId) => {
    if (!channel) return;
    await streamChatService.deleteMessage(messageId, channelId);
  }, [channel, channelId]);

  const updateMessage = useCallback(async (messageId, text) => {
    if (!channel) return;
    await streamChatService.updateMessage(messageId, { text });
  }, [channel]);

  const loadMoreMessages = useCallback(async () => {
    if (!channel || !messages.length) return;

    try {
      const oldestMessage = messages[0];
      const olderMessages = await channel.query({
        messages: { 
          limit: 20,
          id_lt: oldestMessage.id
        }
      });

      if (olderMessages.messages.length > 0) {
        const formattedMessages = olderMessages.messages.map(msg =>
          streamChatService.formatMessageForUI(msg)
        );
        setMessages(prev => [...formattedMessages, ...prev]);
      }
    } catch (err) {
      console.error('Failed to load more messages:', err);
    }
  }, [channel, messages]);

  return {
    channel,
    messages,
    isLoading,
    error,
    typingUsers,
    sendMessage,
    sendTypingEvent,
    stopTypingEvent,
    deleteMessage,
    updateMessage,
    loadMoreMessages
  };
};

export const useTypingIndicator = (channelId, onTyping) => {
  const [isTyping, setIsTyping] = useState(false);
  const [typingTimer, setTypingTimer] = useState(null);

  const handleTyping = useCallback(() => {
    if (!isTyping) {
      setIsTyping(true);
      streamChatService.sendTypingEvent(channelId);
    }

    // Clear existing timer
    if (typingTimer) {
      clearTimeout(typingTimer);
    }

    // Set new timer to stop typing after 3 seconds
    const timer = setTimeout(() => {
      setIsTyping(false);
      streamChatService.stopTypingEvent(channelId);
    }, 3000);

    setTypingTimer(timer);
  }, [channelId, isTyping, typingTimer]);

  useEffect(() => {
    return () => {
      if (typingTimer) {
        clearTimeout(typingTimer);
      }
      if (isTyping) {
        streamChatService.stopTypingEvent(channelId);
      }
    };
  }, [channelId, typingTimer, isTyping]);

  return handleTyping;
};
